/**
 * Hook to manage capturing state history for missed capture filtering
 * Converts capturing state changes into disabled periods
 */

import { useState, useEffect, useMemo } from "react";
import type { CapturingStateEntry, DisabledPeriod } from "@/types";
import { subscribeToCapturingStateHistory } from "@/services/viewService";

interface UseCapturingStateHistoryParams {
  userId: string;
  projectId: string;
  viewId: string;
  rangeStart: Date;
  rangeEnd: Date;
  enabled?: boolean;
}

interface UseCapturingStateHistoryResult {
  capturingHistory: CapturingStateEntry[];
  disabledPeriods: DisabledPeriod[];
  loading: boolean;
  error: Error | null;
}

/**
 * Convert capturing state history into disabled periods
 * A disabled period is when capturing was false
 *
 * Example:
 * - Entry 1: changedAt=10:00, capturing=true (started capturing)
 * - Entry 2: changedAt=14:00, capturing=false (stopped capturing)
 * - Entry 3: changedAt=16:00, capturing=true (started again)
 *
 * This produces one disabled period: { start: 14:00, end: 16:00 }
 *
 * Edge cases:
 * - If the first entry (chronologically) has capturing=false, the period starts from rangeStart
 * - If the last entry has capturing=false, the period extends to rangeEnd
 */
function convertToDisabledPeriods(
  history: CapturingStateEntry[],
  rangeStart: Date,
  rangeEnd: Date
): DisabledPeriod[] {
  if (history.length === 0) {
    // No history - assume captures were always enabled
    return [];
  }

  // Sort chronologically (oldest first)
  const sorted = [...history].sort((a, b) => {
    const aTime = a.changedAt?.toDate?.()?.getTime() || 0;
    const bTime = b.changedAt?.toDate?.()?.getTime() || 0;
    return aTime - bTime;
  });

  const periods: DisabledPeriod[] = [];
  let disabledStart: Date | null = null;

  // Check if we start in a disabled state
  // If the first entry is "capturing=true", it means before that we were disabled
  // But we don't know when - so we assume enabled before first entry
  const firstEntry = sorted[0];
  const firstEntryTime = firstEntry.changedAt?.toDate?.() || rangeStart;

  if (firstEntry.capturing === false && firstEntryTime.getTime() > rangeStart.getTime()) {
    // First entry is disabling, and it's after rangeStart
    // We can't know for sure what state was before, so assume disabled from rangeStart
    // Actually, if the first tracked entry is "capturing=false", it means they turned OFF
    // So before that toggle, captures were ON. We should NOT add a disabled period before.
  }

  // Process each entry
  for (const entry of sorted) {
    const entryTime = entry.changedAt?.toDate?.();
    if (!entryTime) continue;

    // Skip entries outside our range
    if (entryTime.getTime() < rangeStart.getTime()) {
      // Entry is before range - track state but don't create period
      if (entry.capturing === false) {
        disabledStart = rangeStart; // Disabled period starts at range start
      } else {
        disabledStart = null; // Was enabled
      }
      continue;
    }

    if (entryTime.getTime() > rangeEnd.getTime()) {
      // Entry is after range - stop processing
      break;
    }

    if (entry.capturing === false) {
      // Capture was disabled at this time
      if (disabledStart === null) {
        disabledStart = entryTime;
      }
    } else {
      // Capture was enabled at this time
      if (disabledStart !== null) {
        // End the disabled period
        periods.push({
          start: disabledStart,
          end: entryTime,
        });
        disabledStart = null;
      }
    }
  }

  // If we end in a disabled state, extend to rangeEnd
  if (disabledStart !== null) {
    periods.push({
      start: disabledStart,
      end: rangeEnd,
    });
  }

  return periods;
}

/**
 * Hook for loading capturing state history and converting to disabled periods
 */
export function useCapturingStateHistory({
  userId,
  projectId,
  viewId,
  rangeStart,
  rangeEnd,
  enabled = true,
}: UseCapturingStateHistoryParams): UseCapturingStateHistoryResult {
  const [capturingHistory, setCapturingHistory] = useState<CapturingStateEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Subscribe to capturing state history
  useEffect(() => {
    if (!enabled || !userId || !projectId || !viewId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToCapturingStateHistory(
      userId,
      projectId,
      viewId,
      (history) => {
        setCapturingHistory(history);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error loading capturing state history:", err);
        setError(err);
        setLoading(false);
      },
      100 // Load last 100 state changes (should be plenty for 7 days)
    );

    return () => unsubscribe();
  }, [userId, projectId, viewId, enabled]);

  // Convert to disabled periods
  const disabledPeriods = useMemo(() => {
    if (!enabled || loading) {
      return [];
    }
    return convertToDisabledPeriods(capturingHistory, rangeStart, rangeEnd);
  }, [capturingHistory, rangeStart, rangeEnd, enabled, loading]);

  return {
    capturingHistory,
    disabledPeriods,
    loading,
    error,
  };
}
